/**
 * How to connect each AI agent to a tinycms site, step by step, as the
 * onboarding wizard shows them. Kept as data so the wording can be fixed
 * when an agent moves its menus, without touching the wizard.
 *
 * The main path is the same for every agent that can sign a person in:
 * paste the site's address, then sign in with your email in the window
 * that opens. The site hands the agent its own credentials behind the
 * scenes, so there is no Client ID or secret to copy. Agents that still
 * need a key (Grok, Copilot Studio) show it inline; everyone else finds it
 * folded away under "Advanced".
 *
 * A step is text plus, optionally, the connection values it asks for —
 * the wizard shows those values inline with copy buttons, filled in with the
 * person's own address and credentials.
 */

export type AgentId = 'claude' | 'grok' | 'chatgpt' | 'gemini' | 'm365copilot' | 'githubcopilot';

/**
 * Values a step can show: the site's address, the key values for tools
 * that need one, and ready-made VS Code configs (one that signs in, one
 * that carries the key).
 */
export type ConnectionValue =
  | 'url'
  | 'clientId'
  | 'clientSecret'
  | 'token'
  | 'authUrl'
  | 'tokenUrl'
  | 'scope'
  | 'vscodeConfig'
  | 'vscodeTokenConfig';

export interface GuideStep {
  text: string;
  values?: ConnectionValue[];
}

export interface AgentGuide {
  id: AgentId;
  name: string;
  /** One line under the name on the agent button. */
  blurb: string;
  steps: GuideStep[];
  /** A caveat shown under the steps. */
  note?: string;
}

export const VALUE_LABELS: Record<ConnectionValue, string> = {
  url: 'Server address',
  clientId: 'Client ID',
  clientSecret: 'Client secret',
  token: 'Token',
  authUrl: 'Authorization URL',
  tokenUrl: 'Token URL',
  scope: 'Scope',
  vscodeConfig: 'Paste into .vscode/mcp.json',
  vscodeTokenConfig: 'Paste into .vscode/mcp.json (with the key)',
};

export const AGENT_GUIDES: AgentGuide[] = [
  {
    id: 'claude',
    name: 'Claude',
    blurb: 'claude.ai and the Claude apps',
    steps: [
      { text: 'In Claude, open Customize, then Connectors.' },
      { text: 'Choose + Add, then “Add custom connector”, and name it after your site.' },
      { text: 'Paste your server address and choose Continue.', values: ['url'] },
      { text: 'Finish adding it. When a window opens, sign in with your email.' },
      {
        text: 'Start a new chat, turn the connector on from the tools menu, and ask for your first page.',
      },
    ],
    note: 'Custom connectors depend on your Claude plan. On a Team or Enterprise plan, an owner adds the connector in Organization settings → Connectors, and each person then connects to it.',
  },
  {
    id: 'grok',
    name: 'Grok',
    blurb: 'grok.com and the Grok apps',
    steps: [
      { text: 'Go to grok.com/connectors.' },
      {
        text: 'Choose New Connector, then Custom, and paste your server address.',
        values: ['url'],
      },
      {
        text: 'When it asks how to sign in, choose a bearer token and paste this.',
        values: ['token'],
      },
      { text: 'Save, then ask Grok for your first page.' },
    ],
    note: 'If Grok’s menus look different, ask Grok how to add a remote MCP connector.',
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    blurb: 'chatgpt.com, as a plugin',
    steps: [
      {
        text: 'In ChatGPT on the web, open Plugins (chatgpt.com/plugins), choose +, then “Add custom MCP server”.',
      },
      { text: 'Name it after your site and paste your server address.', values: ['url'] },
      { text: 'Under Authentication choose OAuth. There’s nothing else to paste.' },
      { text: 'Accept the warning about custom servers, then choose “Create as a plugin”.' },
      { text: 'Sign in with your email when ChatGPT asks, and choose Connect.' },
      {
        text: 'Install the plugin from your personal plugins. Then type @ in a new chat, pick it, and ask for your first page.',
      },
    ],
    note: 'Needs ChatGPT on the web and a plan that allows custom MCP servers. On a Business or Enterprise workspace, an admin has to allow it.',
  },
  {
    id: 'gemini',
    name: 'Gemini',
    blurb: 'gemini.google.com and the Gemini apps',
    steps: [
      {
        text: 'In Gemini on the web (gemini.google.com), open Settings, then Connected Apps.',
      },
      { text: 'Under “Custom apps”, choose “Add a custom app”.' },
      { text: 'Paste your server address. There’s nothing else to paste.', values: ['url'] },
      { text: 'Choose Next, sign in with your email, then choose Connect.' },
      { text: 'Type @ in a new chat, pick your site, and ask for your first page.' },
    ],
    note: 'Google offers custom apps in the US, in English, to personal Google accounts (not work or school ones), with Keep Activity on. You set it up on the web, then it works in the Gemini apps too.',
  },
  {
    id: 'm365copilot',
    name: 'Microsoft Copilot',
    blurb: 'Microsoft 365 Copilot, through Copilot Studio',
    steps: [
      {
        text: 'In Copilot Studio, open your agent, go to Tools, and add a new tool. Choose Model Context Protocol.',
      },
      { text: 'Name it after your site and paste your server address.', values: ['url'] },
      {
        text: 'For authentication choose OAuth 2.0, then Manual, and paste these.',
        values: ['clientId', 'clientSecret', 'authUrl', 'tokenUrl', 'scope'],
      },
      { text: 'Use the token URL for the refresh URL too, then create the tool.' },
      { text: 'Add it to your agent, sign in when asked, and ask for your first page.' },
    ],
    note: 'Copilot Studio comes with Microsoft 365 work accounts. Your admin may need to allow it.',
  },
  {
    id: 'githubcopilot',
    name: 'GitHub Copilot',
    blurb: 'Copilot in VS Code',
    steps: [
      {
        text: 'In your project, create a file named .vscode/mcp.json and paste this into it.',
        values: ['vscodeConfig'],
      },
      {
        text: 'Save the file, then select Start above the server name. VS Code opens a window: sign in with your email, then choose Connect.',
      },
      { text: 'Open Copilot Chat in Agent mode and ask for your first page.' },
    ],
    note: 'If VS Code can’t open the sign-in window, use the version of this file with the key, under Advanced below.',
  },
];
